/**
 * User Roles management page — Admin only.
 *
 * Lists users with current role assignments and department scopes.
 * Allows editing role assignments and scopes per user.
 *
 * Requirements: 14.2, 14.8, 14.9
 */

import { useState, useEffect, useCallback } from 'react';

interface UserRole {
  assignmentId: number;
  roleName: string;
  departmentScopes: string[];
  createdAt: string;
  createdBy: string | null;
}

interface UserWithRoles {
  user: {
    userId: number;
    email: string;
    displayName: string | null;
    isActive: boolean;
    createdAt: string;
  };
  roles: UserRole[];
}

interface UserListResponse {
  data: UserWithRoles[];
  total: number;
  page: number;
  pageSize: number;
}

const ALL_ROLES = [
  'Admin',
  'IT Admin',
  'Contract Specialist',
  'Facilities Admin',
  'Accounting',
  'Controller',
  'CFO',
  'VP Approver',
  'Service Desk',
];

const ALL_DEPARTMENTS = ['IT', 'Facilities', 'Finance', 'HR', 'Marketing', 'Operations', 'Legal', 'Purchasing', 'Executive'];

export function UserRoles() {
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editRoles, setEditRoles] = useState<Array<{ roleName: string; departmentScopes: string[] }>>([]);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRoles, setNewRoles] = useState<string[]>([]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '25' });
      if (search) params.set('search', search);

      const res = await fetch(`/api/mgmt/users?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load users.');
      const data: UserListResponse = await res.json();
      setUsers(data.data);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const startEditing = (user: UserWithRoles) => {
    setEditingUserId(user.user.userId);
    setEditRoles(
      user.roles.map((r) => ({
        roleName: r.roleName,
        departmentScopes: [...r.departmentScopes],
      })),
    );
  };

  const cancelEditing = () => {
    setEditingUserId(null);
    setEditRoles([]);
  };

  const toggleRole = (roleName: string) => {
    setEditRoles((prev) => {
      const existing = prev.find((r) => r.roleName === roleName);
      if (existing) {
        return prev.filter((r) => r.roleName !== roleName);
      }
      return [...prev, { roleName, departmentScopes: [] }];
    });
  };

  const toggleScope = (roleName: string, scope: string) => {
    setEditRoles((prev) =>
      prev.map((r) => {
        if (r.roleName !== roleName) return r;
        const hasScope = r.departmentScopes.includes(scope);
        return {
          ...r,
          departmentScopes: hasScope
            ? r.departmentScopes.filter((s) => s !== scope)
            : [...r.departmentScopes, scope],
        };
      }),
    );
  };

  const saveRoles = async () => {
    if (!editingUserId) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/mgmt/users/${editingUserId}/roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ roles: editRoles }),
      });
      if (!res.ok) throw new Error('Failed to save roles.');
      setEditingUserId(null);
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div >
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-lg font-semibold text-text-primary">User Role Management</h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium hover:bg-baker-red/90"
        >
          {showAddForm ? 'Cancel' : '+ Add User'}
        </button>
      </div>

      {/* Add User Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg border border-border p-5 mb-5">
          <h2 className="text-sm font-semibold mb-3">Add New User</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <input
              type="email"
              placeholder="UPN (login name, e.g. jsmith@bakerdist.com)"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="py-2 px-3 rounded-lg border border-border text-sm"
            />
            <input
              type="text"
              placeholder="Display name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="py-2 px-3 rounded-lg border border-border text-sm"
            />
            <button
              onClick={async () => {
                if (!newEmail) return;
                try {
                  const res = await fetch('/api/mgmt/users', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: newEmail, displayName: newName, roles: newRoles }),
                  });
                  if (!res.ok) { const d = await res.json(); throw new Error(d.error?.message || 'Failed'); }
                  setShowAddForm(false);
                  setNewEmail('');
                  setNewName('');
                  setNewRoles([]);
                  fetchUsers();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Failed to add user');
                }
              }}
              className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium hover:bg-baker-red/90"
            >
              Create User
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {ALL_ROLES.map(role => (
              <button
                key={role}
                onClick={() => setNewRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role])}
                className={`px-3 py-1 rounded border text-xs ${newRoles.includes(role) ? 'bg-blue-100 border-blue-400 text-blue-800' : 'bg-white border-gray-300 text-gray-600'}`}
              >
                {role}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-5">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name or email..."
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-72"
          aria-label="Search users"
        />
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm mb-4">{error}</div>}

      {/* User List */}
      <div className="space-y-4">
        {loading ? (
          <p className="text-slate-400">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="text-slate-400">No users found.</p>
        ) : (
          users.map((u) => (
            <div key={u.user.userId} className="bg-white rounded-lg border border-border p-5">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-text-primary">{u.user.displayName ?? u.user.email}</div>
                  <div className="text-sm text-slate-500">{u.user.email}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {u.roles.map((r) => (
                      <span key={r.assignmentId} className="px-2.5 py-0.5 bg-baker-accent/20 text-text-primary rounded-full text-xs font-medium">
                        {r.roleName}
                        {r.departmentScopes.length > 0 && ` (${r.departmentScopes.join(', ')})`}
                      </span>
                    ))}
                    {u.roles.length === 0 && (
                      <span className="text-xs text-slate-400">No roles assigned</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => editingUserId === u.user.userId ? cancelEditing() : startEditing(u)}
                  className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page"
                >
                  {editingUserId === u.user.userId ? 'Cancel' : 'Edit'}
                </button>
                <button
                  onClick={async () => {
                    if (!confirm(`Deactivate ${u.user.displayName || u.user.email}?`)) return;
                    await fetch(`/api/mgmt/users/${u.user.userId}`, { method: 'DELETE', credentials: 'include' });
                    fetchUsers();
                  }}
                  className="px-4 py-2 rounded-lg border border-red-200 bg-white text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  Deactivate
                </button>
              </div>

              {/* Edit Panel */}
              {editingUserId === u.user.userId && (
                <div className="mt-4 border-t pt-4">
                  <div className="text-sm font-medium mb-2">Assign Roles:</div>
                  <div className="space-y-3">
                    {ALL_ROLES.map((role) => {
                      const isSelected = editRoles.some((r) => r.roleName === role);
                      const roleEntry = editRoles.find((r) => r.roleName === role);
                      return (
                        <div key={role}>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRole(role)}
                            />
                            <span className="text-sm">{role}</span>
                          </label>
                          {isSelected && (
                            <div className="ml-6 mt-1 flex flex-wrap gap-2">
                              {ALL_DEPARTMENTS.map((dept) => (
                                <label key={dept} className="flex items-center gap-1 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={roleEntry?.departmentScopes.includes(dept) ?? false}
                                    onChange={() => toggleScope(role, dept)}
                                  />
                                  {dept}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={saveRoles}
                      disabled={saving}
                      className="px-4 py-2 bg-baker-red text-white rounded-lg text-sm font-medium hover:bg-baker-red/90 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Roles'}
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {total > 25 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-slate-500">Page {page}</span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={users.length < 25}
            className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
